import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { getApiUrl } from '../config/api';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell
} from 'recharts';
import './DashboardVentas.css';

const hoy = () => new Date().toISOString().split('T')[0];

const fmt = (v, dec = 0) => {
    if (v == null || v === '') return '0';
    return Number(v).toLocaleString('es-PY', {
        minimumFractionDigits: dec, maximumFractionDigits: dec
    });
};

const fmtFecha = (f) => {
    if (!f) return '-';
    const [y, m, d] = String(f).split('T')[0].split('-');
    return `${d}/${m}/${y}`;
};

// Colores para el aging (de rojo=vencido a verde=lejano)
const COLOR_AGING = {
    'Vencido':          '#C62828',
    '0-30 dias':        '#EF6C00',
    '31-60 dias':       '#F9A825',
    '61-90 dias':       '#9E9D24',
    '+90 dias':         '#2E7D32',
    'Sin vencimiento':  '#9E9E9E'
};

function DashboardCtaPag({ usuario }) {
    const [fechaCorte, setFechaCorte] = useState(hoy());
    const [data, setData] = useState(null);
    const [cargando, setCargando] = useState(false);

    // === Grilla de facturas pendientes (busqueda / paginacion / orden / filtro / export) ===
    const [pendientes, setPendientes] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [pendDesde, setPendDesde] = useState('');
    const [pendHasta, setPendHasta] = useState('');
    const [sortCol, setSortCol] = useState('vence');
    const [sortDir, setSortDir] = useState('asc');   // asc | desc
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(20);

    useEffect(() => { cargar(); cargarPendientes(); /* eslint-disable-next-line */ }, []);

    const cargarPendientes = async () => {
        try {
            const params = {};
            if (pendDesde) params.desde = pendDesde;
            if (pendHasta) params.hasta = pendHasta;
            const { data } = await axios.get(getApiUrl('/api/ctapag/facturas-pendientes'), { params });
            setPendientes(data);
            setPagina(1);
        } catch (e) {
            console.error(e);
        }
    };

    // Ordenamiento client-side
    const ordenarPor = (col) => {
        if (sortCol === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortCol(col);
            setSortDir('asc');
        }
        setPagina(1);
    };

    // Filtro de texto client-side (proveedor, nro factura, tipo doc, moneda, cod)
    const b = busqueda.trim().toLowerCase();
    const pendientesFiltradas = !b ? pendientes : pendientes.filter(v =>
        String(v.nom_proveedor || '').toLowerCase().includes(b)
        || String(v.id_proveedor || '').includes(b)
        || String(v.nfactura || '').includes(b)
        || String(v.tipo_doc || '').toLowerCase().includes(b)
        || String(v.moneda || '').toLowerCase().includes(b)
    );

    const NUM_COLS = new Set(['nfactura', 'dias', 'saldo', 'saldo_gs', 'mon_total', 'cambio', 'id_proveedor']);
    const pendientesOrdenadas = [...pendientesFiltradas].sort((a, b) => {
        let va = a[sortCol], vb = b[sortCol];
        if (NUM_COLS.has(sortCol)) { va = Number(va) || 0; vb = Number(vb) || 0; }
        else { va = String(va ?? '').toLowerCase(); vb = String(vb ?? '').toLowerCase(); }
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const totalPaginas = Math.max(1, Math.ceil(pendientesOrdenadas.length / porPagina));
    const pActual = Math.min(pagina, totalPaginas);
    const paginadas = pendientesOrdenadas.slice((pActual - 1) * porPagina, pActual * porPagina);
    const totalSaldoGsFiltrado = pendientesOrdenadas.reduce((s, r) => s + (Number(r.saldo_gs) || 0), 0);

    const exportarPendientes = () => {
        const filas = pendientesOrdenadas.map(v => ({
            'Cod Prov': v.id_proveedor,
            'Proveedor': v.nom_proveedor,
            'Tipo Doc': v.tipo_doc,
            'Nro Factura': v.nfactura,
            'Fecha': fmtFecha(v.fecha),
            'Vence': fmtFecha(v.vence),
            'Dias': v.dias,
            'Moneda': v.moneda,
            'Cambio': Number(v.cambio),
            'Saldo (orig.)': Number(v.saldo),
            'Saldo (Gs.)': Number(v.saldo_gs)
        }));
        const ws = XLSX.utils.json_to_sheet(filas);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Pendientes');
        XLSX.writeFile(wb, `facturas_pendientes_${hoy()}.xlsx`);
    };

    // Indicador de flecha en el header
    const flecha = (col) => sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const cargar = async () => {
        setCargando(true);
        try {
            const { data } = await axios.get(getApiUrl('/api/ctapag/dashboard'), {
                params: { fechaCorte }
            });
            setData(data);
        } catch (e) {
            console.error(e);
            alert('Error al cargar el dashboard');
        } finally {
            setCargando(false);
        }
    };

    const k = data?.kpis || {};
    const aging = (data?.aging || []).map(a => ({
        ...a, monto: Number(a.monto), cant: Number(a.cant)
    }));
    const top = (data?.topProveedores || []).map(t => ({
        nombre: String(t.nom_proveedor || '').substring(0, 22),
        saldo: Number(t.saldo), cant: Number(t.cant)
    }));
    const pagos = (data?.evolucionPagos || []).map(p => ({
        mes: p.mes, total: Number(p.total), cant: Number(p.cant)
    }));
    const nc = data?.ncPorProveedor || [];
    const porMoneda = data?.porMoneda || [];
    // ¿Hay más de una moneda con saldo? (para mostrar avisos de conversión)
    const multimoneda = porMoneda.length > 1;

    return (
        <div className="dashboard-ventas-container">
            <h2 className="dashboard-ventas-titulo">Dashboard de Cuentas a Pagar</h2>

            {/* Filtro fecha corte */}
            <div className="dashboard-filtros">
                <label>Fecha de corte:
                    <input type="date" value={fechaCorte}
                        onChange={(e) => setFechaCorte(e.target.value)}
                        style={{ marginLeft: '6px', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                </label>
                <button onClick={cargar} style={{
                    marginLeft: '10px', padding: '8px 18px', background: '#004F9F', color: '#fff',
                    border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
                }}>Actualizar</button>
            </div>

            {cargando && <div className="dashboard-loading">Cargando...</div>}

            {data && !cargando && (
                <>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                        💱 Todos los totales estan expresados en <strong>Guaranies (Gs.)</strong>,
                        convirtiendo cada documento por su tipo de cambio.
                        {multimoneda && ' Ver desglose por moneda mas abajo.'}
                    </div>

                    {/* ===== KPIs ===== */}
                    <div className="dashboard-kpi-grid">
                        <div className="dashboard-kpi-card" style={{ borderLeft: '4px solid #004F9F' }}>
                            <div className="dashboard-kpi-label">Total a Pagar (Gs.)</div>
                            <div className="dashboard-kpi-valor">{fmt(k.totalPagar)}</div>
                        </div>
                        <div className="dashboard-kpi-card" style={{ borderLeft: '4px solid #2E7D32' }}>
                            <div className="dashboard-kpi-label">Notas de Credito disp. (Gs.)</div>
                            <div className="dashboard-kpi-valor" style={{ color: '#2E7D32' }}>
                                {fmt(k.ncDisponible)}
                                <span style={{ fontSize: '12px', color: '#888' }}> ({k.cantNc || 0})</span>
                            </div>
                        </div>
                        <div className="dashboard-kpi-card" style={{ borderLeft: '4px solid #1565C0' }}>
                            <div className="dashboard-kpi-label">Deuda Neta (Gs.)</div>
                            <div className="dashboard-kpi-valor" style={{ color: '#1565C0' }}>{fmt(k.deudaNeta)}</div>
                        </div>
                        <div className="dashboard-kpi-card" style={{ borderLeft: '4px solid #C62828' }}>
                            <div className="dashboard-kpi-label">Vencido</div>
                            <div className="dashboard-kpi-valor" style={{ color: '#C62828' }}>{fmt(k.vencido)}</div>
                        </div>
                        <div className="dashboard-kpi-card" style={{ borderLeft: '4px solid #EF6C00' }}>
                            <div className="dashboard-kpi-label">Por Vencer</div>
                            <div className="dashboard-kpi-valor" style={{ color: '#EF6C00' }}>{fmt(k.porVencer)}</div>
                        </div>
                        <div className="dashboard-kpi-card" style={{ borderLeft: '4px solid #607D8B' }}>
                            <div className="dashboard-kpi-label">Facturas / Proveedores</div>
                            <div className="dashboard-kpi-valor">
                                {k.cantFacturas || 0}<span style={{ fontSize: '14px', color: '#888' }}> / {k.cantProveedores || 0}</span>
                            </div>
                        </div>
                    </div>

                    {/* ===== Totales por moneda ===== */}
                    <div className="dashboard-seccion">
                        <h3>Saldo pendiente por moneda</h3>
                        <table className="dashboard-tabla" style={{ maxWidth: '600px' }}>
                            <thead><tr>
                                <th>Moneda</th>
                                <th style={{ textAlign: 'right' }}>Facturas</th>
                                <th style={{ textAlign: 'right' }}>Saldo (moneda original)</th>
                                <th style={{ textAlign: 'right' }}>Equivalente en Gs.</th>
                            </tr></thead>
                            <tbody>
                                {porMoneda.map(m => (
                                    <tr key={m.moneda}>
                                        <td>
                                            <span style={{
                                                padding: '2px 8px', borderRadius: '10px', fontWeight: 600, fontSize: '12px',
                                                background: m.moneda === 'GS' ? '#E4F2FC' : '#FFF3E0',
                                                color: m.moneda === 'GS' ? '#004F9F' : '#EF6C00'
                                            }}>{m.moneda}</span>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{m.cant}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            {m.moneda === 'GS' ? fmt(m.saldo_orig) : fmt(m.saldo_orig, 2)}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(m.saldo_gs)}</td>
                                    </tr>
                                ))}
                                {porMoneda.length === 0 && (
                                    <tr><td colSpan="4" style={{ textAlign: 'center', color: '#888', padding: '12px' }}>Sin saldos</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ===== Aging + Top proveedores ===== */}
                    <div className="dashboard-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="dashboard-seccion">
                            <h3>Antiguedad de saldos (aging)</h3>
                            {aging.every(a => a.monto === 0) ? (
                                <div className="dashboard-sin-datos">Sin saldos pendientes</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={aging} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="rango" tick={{ fontSize: 11 }} />
                                        <YAxis tickFormatter={(v) => fmt(v / 1000) + 'k'} tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={(v) => fmt(v)} />
                                        <Bar dataKey="monto" name="Saldo">
                                            {aging.map((a, i) => (
                                                <Cell key={i} fill={COLOR_AGING[a.rango] || '#888'} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                            {/* Mini tabla del aging */}
                            <table className="dashboard-tabla" style={{ marginTop: '10px', fontSize: '12px' }}>
                                <thead><tr><th>Rango</th><th style={{ textAlign: 'right' }}>Saldo</th><th style={{ textAlign: 'right' }}>Facturas</th></tr></thead>
                                <tbody>
                                    {aging.map(a => (
                                        <tr key={a.rango}>
                                            <td><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                                                background: COLOR_AGING[a.rango], marginRight: 6 }}></span>{a.rango}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(a.monto)}</td>
                                            <td style={{ textAlign: 'right' }}>{a.cant}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="dashboard-seccion">
                            <h3>Top 10 proveedores por saldo</h3>
                            {top.length === 0 ? (
                                <div className="dashboard-sin-datos">Sin datos</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={360}>
                                    <BarChart data={top} layout="vertical"
                                        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" tickFormatter={(v) => fmt(v / 1000) + 'k'} tick={{ fontSize: 10 }} />
                                        <YAxis type="category" dataKey="nombre" width={130} tick={{ fontSize: 10 }} />
                                        <Tooltip formatter={(v) => fmt(v)} />
                                        <Bar dataKey="saldo" name="Saldo" fill="#004F9F" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* ===== Evolucion de pagos ===== */}
                    <div className="dashboard-seccion">
                        <h3>Evolucion de pagos (ultimos 12 meses)</h3>
                        {pagos.length === 0 ? (
                            <div className="dashboard-sin-datos">Sin pagos registrados</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={260}>
                                <LineChart data={pagos} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                                    <YAxis tickFormatter={(v) => fmt(v / 1000) + 'k'} tick={{ fontSize: 11 }} />
                                    <Tooltip formatter={(v) => fmt(v)} />
                                    <Line type="monotone" dataKey="total" name="Pagado" stroke="#2E7D32" strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* ===== NC por proveedor (si hay) ===== */}
                    {nc.length > 0 && (
                        <div className="dashboard-seccion">
                            <h3>Notas de credito disponibles por proveedor</h3>
                            <table className="dashboard-tabla">
                                <thead><tr>
                                    <th>Proveedor</th>
                                    <th style={{ textAlign: 'right' }}>Cant</th>
                                    <th style={{ textAlign: 'right' }}>Saldo NC</th>
                                </tr></thead>
                                <tbody>
                                    {nc.map(n => (
                                        <tr key={n.id_proveedor}>
                                            <td>{n.id_proveedor} - {n.nom_proveedor}</td>
                                            <td style={{ textAlign: 'right' }}>{n.cant}</td>
                                            <td style={{ textAlign: 'right', color: '#2E7D32', fontWeight: 600 }}>{fmt(n.saldo)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ===== Grilla de facturas pendientes (filtro/orden/pagina/export) ===== */}
                    <div className="dashboard-seccion">
                        <h3>Facturas pendientes de pago</h3>

                        {/* Buscador + filtros por vencimiento + export */}
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'end', flexWrap: 'wrap', marginBottom: '10px' }}>
                            <div style={{ position: 'relative' }}>
                                <input type="text" placeholder="🔍 Buscar proveedor / nro / doc / moneda"
                                    value={busqueda}
                                    onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
                                    style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '280px' }} />
                                {busqueda && (
                                    <button onClick={() => { setBusqueda(''); setPagina(1); }}
                                        title="Limpiar busqueda"
                                        style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)',
                                                 background: 'none', border: 'none', color: '#C62828', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                                )}
                            </div>
                            <label style={{ fontSize: '12px', color: '#555' }}>Vence desde:
                                <input type="date" value={pendDesde} onChange={(e) => setPendDesde(e.target.value)}
                                    style={{ marginLeft: '5px', padding: '5px', border: '1px solid #ddd', borderRadius: '4px' }} />
                            </label>
                            <label style={{ fontSize: '12px', color: '#555' }}>Vence hasta:
                                <input type="date" value={pendHasta} onChange={(e) => setPendHasta(e.target.value)}
                                    style={{ marginLeft: '5px', padding: '5px', border: '1px solid #ddd', borderRadius: '4px' }} />
                            </label>
                            <button onClick={cargarPendientes} style={{
                                padding: '7px 16px', background: '#004F9F', color: '#fff',
                                border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
                            }}>Filtrar</button>
                            {(pendDesde || pendHasta) && (
                                <button onClick={() => { setPendDesde(''); setPendHasta(''); setTimeout(cargarPendientes, 0); }}
                                    style={{ padding: '7px 12px', background: '#f5f5f5', border: '1px solid #ddd',
                                             borderRadius: '4px', cursor: 'pointer' }}>Limpiar</button>
                            )}
                            <button onClick={exportarPendientes} disabled={pendientesOrdenadas.length === 0}
                                style={{ padding: '7px 16px', background: '#4CAF50', color: '#fff',
                                         border: 'none', borderRadius: '4px',
                                         cursor: pendientesOrdenadas.length ? 'pointer' : 'not-allowed',
                                         opacity: pendientesOrdenadas.length ? 1 : 0.5 }}>📊 Exportar Excel</button>
                            <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#333' }}>
                                {pendientesOrdenadas.length} factura(s) · Total Gs. <strong>{fmt(totalSaldoGsFiltrado)}</strong>
                            </span>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            <table className="dashboard-tabla">
                                <thead><tr>
                                    <th onClick={() => ordenarPor('id_proveedor')} style={thSort}>Cod{flecha('id_proveedor')}</th>
                                    <th onClick={() => ordenarPor('nom_proveedor')} style={thSort}>Proveedor{flecha('nom_proveedor')}</th>
                                    <th onClick={() => ordenarPor('tipo_doc')} style={thSort}>Doc{flecha('tipo_doc')}</th>
                                    <th onClick={() => ordenarPor('nfactura')} style={{ ...thSort, textAlign: 'right' }}>N° Fact{flecha('nfactura')}</th>
                                    <th onClick={() => ordenarPor('fecha')} style={thSort}>Fecha{flecha('fecha')}</th>
                                    <th onClick={() => ordenarPor('vence')} style={thSort}>Vence{flecha('vence')}</th>
                                    <th onClick={() => ordenarPor('dias')} style={{ ...thSort, textAlign: 'center' }}>Dias{flecha('dias')}</th>
                                    <th onClick={() => ordenarPor('moneda')} style={{ ...thSort, textAlign: 'center' }}>Mon{flecha('moneda')}</th>
                                    <th onClick={() => ordenarPor('saldo')} style={{ ...thSort, textAlign: 'right' }}>Saldo (orig.){flecha('saldo')}</th>
                                    <th onClick={() => ordenarPor('saldo_gs')} style={{ ...thSort, textAlign: 'right' }}>Saldo (Gs.){flecha('saldo_gs')}</th>
                                </tr></thead>
                                <tbody>
                                    {paginadas.map((v, i) => {
                                        const dias = v.dias == null ? null : Number(v.dias);
                                        const vencido = dias != null && dias < 0;
                                        const esGs = v.moneda === 'GS';
                                        return (
                                            <tr key={i}>
                                                <td>{v.id_proveedor}</td>
                                                <td style={{ fontSize: '12px' }}>{v.nom_proveedor}</td>
                                                <td>{v.tipo_doc}</td>
                                                <td style={{ textAlign: 'right' }}>{v.nfactura}</td>
                                                <td>{fmtFecha(v.fecha)}</td>
                                                <td>{fmtFecha(v.vence)}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {dias == null ? '-' : (
                                                        <span style={{
                                                            padding: '1px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: 600,
                                                            background: vencido ? '#FFEBEE' : (dias <= 15 ? '#FFF3E0' : '#E8F5E9'),
                                                            color: vencido ? '#C62828' : (dias <= 15 ? '#EF6C00' : '#2E7D32')
                                                        }}>{vencido ? `${dias}` : `+${dias}`}</span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'center', fontSize: '11px',
                                                             color: esGs ? '#004F9F' : '#EF6C00', fontWeight: 600 }}>{v.moneda}</td>
                                                <td style={{ textAlign: 'right' }}>{esGs ? fmt(v.saldo) : fmt(v.saldo, 2)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(v.saldo_gs)}</td>
                                            </tr>
                                        );
                                    })}
                                    {paginadas.length === 0 && (
                                        <tr><td colSpan="10" style={{ textAlign: 'center', color: '#888', padding: '15px' }}>
                                            Sin facturas pendientes
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Paginacion */}
                        {pendientesOrdenadas.length > 0 && (
                            <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button disabled={pActual === 1} onClick={() => setPagina(1)}>«</button>
                                <button disabled={pActual === 1} onClick={() => setPagina(p => p - 1)}>‹</button>
                                <span style={{ fontSize: '13px' }}>Pagina {pActual} de {totalPaginas}</span>
                                <button disabled={pActual === totalPaginas} onClick={() => setPagina(p => p + 1)}>›</button>
                                <button disabled={pActual === totalPaginas} onClick={() => setPagina(totalPaginas)}>»</button>
                                <select value={porPagina} onChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(1); }}
                                    style={{ marginLeft: '8px', padding: '4px' }}>
                                    {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / pag</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

// Header clickeable para ordenar
const thSort = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

export default DashboardCtaPag;
